import type { TenantSettings, TokenPrices, UsageSummary } from '@rp/contracts'
import type { UseQueryResult } from '@tanstack/react-query'
import { isApiError } from '../../api/client'
import { Card } from '../../design'
import { LOCALE } from '../../lib/time'
import { estimateCost, formatUsd } from './cost'
import { USAGE_COPY } from './copy'
import { PriceForm } from './PriceForm'

const numberFormat = new Intl.NumberFormat(LOCALE)

interface CostEstimateProps {
  usage: UsageSummary
  settings: UseQueryResult<TenantSettings>
}

const Estimate = ({ usage, prices }: { usage: UsageSummary; prices: TenantSettings['tokenPrices'] }) => {
  const estimate = estimateCost(usage.byProvider, prices, usage.budget.monthlyTokenBudget)
  return (
    <>
      <dl className="usage-figures">
        <div className="usage-figure">
          <dt>{USAGE_COPY.costMonth}</dt>
          <dd className="num">{USAGE_COPY.costAbout(formatUsd(estimate.monthCostUsd))}</dd>
        </div>
        <div className="usage-figure">
          <dt>{USAGE_COPY.costBudget}</dt>
          <dd className="num">
            {estimate.budgetCostUsd === null
              ? USAGE_COPY.costBudgetUnknown
              : USAGE_COPY.costAbout(formatUsd(estimate.budgetCostUsd))}
          </dd>
        </div>
      </dl>
      {estimate.unpricedTokens === 0 ? null : (
        <p className="usage-updated">{USAGE_COPY.costUnpriced(numberFormat.format(estimate.unpricedTokens))}</p>
      )}
    </>
  )
}

const reloadPrices = async (settings: UseQueryResult<TenantSettings>): Promise<TokenPrices | null> => {
  const result = await settings.refetch()
  return result.isError || result.data === undefined ? null : result.data.tokenPrices
}

const CostBody = ({ usage, settings }: CostEstimateProps) => {
  if (settings.data === undefined) {
    if (!settings.isError) return <p className="usage-updated">{USAGE_COPY.pricesLoading}</p>
    const message = isApiError(settings.error) ? settings.error.message : USAGE_COPY.costSettingsFailed
    return (
      <p className="text-tone tone-danger" role="alert">
        {message}
      </p>
    )
  }
  const prices = settings.data.tokenPrices
  const hasPrices = Object.keys(prices).length > 0
  return (
    <>
      {hasPrices ? <Estimate usage={usage} prices={prices} /> : <p>{USAGE_COPY.costNeedsPrices}</p>}
      <p className="usage-updated">{USAGE_COPY.costHow}</p>
      <p className="usage-updated">{USAGE_COPY.costDisclaimer}</p>
      <PriceForm prices={prices} reload={() => reloadPrices(settings)} />
    </>
  )
}

export const CostEstimate = ({ usage, settings }: CostEstimateProps) => (
  <Card className="usage-card">
    <section aria-label={USAGE_COPY.costHeading} className="usage-cost">
      <h2>{USAGE_COPY.costHeading}</h2>
      <CostBody usage={usage} settings={settings} />
    </section>
  </Card>
)
